import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import { Badge, Dropdown, OverlayTrigger, Table } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { AiOutlineFunction } from "react-icons/ai";
import { BiChevronRight, BiInfoCircle, BiDotsHorizontalRounded, BiBullseye, BiTachometer } from "react-icons/bi";
import { BsThreeDots } from "react-icons/bs";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { FcFinePrint } from "react-icons/fc";
import { useNavigate } from "react-router-dom";
import { LoaderContext } from "../../../App";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import PaginationComponent from "../../../components/Pagination/Pagination";
import { DEFAULT_ITEMS_PER_PAGE, LS_AUTHORITY_KEY, ReportedFrequencyList, STATUS } from "../../../core/constants/common";
import KPIService from "../../../core/service/KPIService";
import Utils from "../../../core/utilities/Utils";
import "./KPI.scss";
import KPIActionModal from "./KPIActionModal";
import KPICategoryModal from "./KPICategoryModal";
import KPIGoalModal from "./KPIGoalModal";
import KPIHeader from "./KPIHeader";
import KPIModal from "./KPIModal";

const KPIList = ({ t }) => {

	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const companyId = lsData.profile.company.id;
	const navigate = useNavigate();
	const { setLoaderDisplay } = useContext(LoaderContext);
	const [goals, setGoals] = useState({});
	const [catVisibility, setCatVisibility] = useState({});
	const [goalVisibility, setGoalVisibility] = useState({});
	const [itemsTotalCount, setItemTotalCount] = useState(0);
	const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
	const [currentPage, setCurrentPage] = useState(1);
	const [editedCategory, setEditedCategory] = useState({ data: { id: -1 }, action: 'NONE' });
	const [editedGoal, setEditedGoal] = useState({ data: { id: -1 }, action: 'NONE' });
	const [editedKPI, setEditedKPI] = useState({ data: { id: -1 }, action: 'NONE' });
	const [editedAction, setEditedAction] = useState({ data: { id: -1 }, action: 'NONE' });
	const [kpis, setKPIs] = useState({});

	useEffect(() => {
		fetchGoals();
	}, []);

	const fetchGoals = (pageNumber, itemsPerPageVal) => {
		let pageNum, perPage;
		if (pageNumber > 0) {
			pageNum = pageNumber - 1;
		} else {
			setCurrentPage(1);
		}
		if (itemsPerPageVal > 0) {
			perPage = itemsPerPageVal;
		} else {
			perPage = itemsPerPage;
		}
		setLoaderDisplay(true);
		KPIService.listGoalsPaged(companyId, 'SHORT_TERM', pageNum, perPage).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			if (res.data.data) {
				setItemTotalCount(res.data.data.totalElements);
				const groupedData = res.data.data.content?.reduce((acc, obj) => {
					if (!acc[obj.category]) {
						acc[obj.category] = {
							category: obj.category,
							categoryId: obj.categoryId,
							items: []
						};
					}
					acc[obj.category].items.push(obj);
					return acc;
				}, {});
				setGoals(groupedData);
			} else {
				setItemTotalCount(0);
				setGoals([]);
			}
		}).catch(err => { setLoaderDisplay(false); });
	};

	const renderTableHeader = () => {
		return (
			<thead>
				<tr>
					<th>{t("Goal.label")}</th>
					<th>{t("Timeline.label")}</th>
					<th>{t("Measured.label")}</th>
					<th>{t("Target.label")}</th>
					<th>{t("RedFlag.label")}</th>
					<th>{t("Entities.label")}</th>
					<th>{t("Actions.label")}</th>
					<th>{t("Status.label")}</th>
					<th></th>
				</tr>
			</thead>
		);
	};

	const showOrHideCategory = (cat) => {
		const vis = { ...catVisibility };
		vis[cat] = vis[cat] === 'hide' ? 'show' : 'hide';
		setCatVisibility(vis);
	};

	const showOrHideGoal = (goalId) => {
		const vis = { ...goalVisibility };
		vis[goalId] = !vis[goalId] || vis[goalId] === 'hide' ? 'show' : 'hide';
		setGoalVisibility(vis);
		if (vis[goalId] === 'hide') return;

		fetchKPIsForGoal(goalId);
	};

	const renderGoalKPIRow = (goalId, kpi, kpiIndex) => {
		return (
			<>
				<tr key={`kpiRow-${goalId}-${kpiIndex}`}
					className={goalVisibility[goalId] === 'hide' ? 'kpi-row d-none' : 'kpi-row'}>
					<td className="name-cell">
						<div className="d-flex justify-content-between">
							<div> <BiTachometer className="me-2 text-primary" /> {kpi.name}</div>
							<div className="text-right">
								<OverlayTrigger
									placement="top"
									overlay={Utils.renderTooltip(kpi.equation.replace(/[DM]/g, ''))}>
									<span className="cursor px-2 text-muted"><AiOutlineFunction className="cursor" /></span>
								</OverlayTrigger>
								<OverlayTrigger
									placement="top"
									overlay={Utils.renderTooltip(kpi.description)}>
									<span>
										<BiInfoCircle className="cursor" />
									</span>
								</OverlayTrigger>
							</div>
						</div>
					</td>
					<td>{kpi.timelineStart ? moment(kpi.timelineStart).format('MMM DD, YYYY') : ''} - {kpi.timelineEnd ? moment(kpi.timelineEnd).format('MMM DD, YYYY') : ''}</td>
					<td>{ReportedFrequencyList?.find(f => f.key === kpi.measured)?.text ?? ''}</td>
					<td>{kpi.target} {kpi.unit}</td>
					<td>{kpi.redFlag} {kpi.unit}</td>
					<td>{kpi.assignees?.length} {kpi.assignees?.length > 0 && <BiChevronRight />}</td>
					<td>

						{kpi.actions?.length > 0 &&
							<span className="Actions_KpiBtn cursor" onClick={() => setEditedAction({ data: { id: 0, kpiId: kpi.id }, action: 'VIEW_ACTIONS' })}>
								<span>
									{kpi.actions?.length}
									<BiChevronRight />
								</span>
							</span>
						}
					</td>
					<td>
						<Badge bg={kpi.status === 'COMPLETED' ? 'badge-Published' : kpi.status === 'IN_PROGRESS' ? 'badge-Inprogress' : 'badge-Draft'}>
							{STATUS[kpi.status]}
						</Badge>
					</td>
					<td>
						<Dropdown>
							<Dropdown.Toggle variant="blank_outline">
								<BiDotsHorizontalRounded />
							</Dropdown.Toggle>
							<Dropdown.Menu>
								<Dropdown.Item onClick={() => setEditedAction({ data: { id: 0, kpiId: kpi.id, goalId: goalId }, action: 'ADD' })}>{t("NewAction.label")}</Dropdown.Item>
								<Dropdown.Item onClick={() => setEditedKPI({ data: { ...kpi, goalId: goalId }, action: 'UPDATE' })}>{t("Edit.label")}</Dropdown.Item>
								<Dropdown.Item onClick={() => setEditedKPI({ data: kpi, action: 'DELETE' })}>{t("Delete.label")}</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					</td>
				</tr>
			</>
		);
	};

	const renderGoalRow = (cat, catIndex, goal, goalIndex) => {
		return (
			<>
				<tr key={`goalRow-${catIndex}-${goalIndex}`}
					className={catVisibility[cat] === 'hide' ? 'goal-row d-none' : 'goal-row'}>
					<td className="name-cell">
						<div className="d-flex justify-content-between">
							<div className="d-flex justify-content-start">
								<span className="mx-2 cursor" onClick={() => showOrHideGoal(goal.id)}>
									{goalVisibility[goal.id] === 'show' ? <FaChevronUp /> : <FaChevronDown />}
								</span>
								<span className="fw-600"><BiBullseye className="me-2 text-success" />{goal.name}</span>
							</div>
							<div className="text-right">
								<OverlayTrigger
									placement="top"
									overlay={Utils.renderTooltip(goal.description)}>
									<span>
										<BiInfoCircle
											className="cursor" />
									</span>
								</OverlayTrigger>
							</div>
						</div>
					</td>
					<td>
						<span className="nowrap">
							{goal.timelineStart ? moment(goal.timelineStart).format('MMM DD, YYYY') : ''} - {goal.timelineEnd ? moment(goal.timelineEnd).format('MMM DD, YYYY') : ''}
						</span>
					</td>
					<td colSpan="5"></td>
					<td>
						<Badge bg={goal.status === 'COMPLETED' ? 'badge-Published' : goal.status === 'IN_PROGRESS' ? 'badge-Inprogress' : 'badge-Draft'}>
							{STATUS[goal.status]}
						</Badge>
					</td>
					<td>
						<Dropdown>
							<Dropdown.Toggle variant="blank_outline">
								<BiDotsHorizontalRounded />
							</Dropdown.Toggle>
							<Dropdown.Menu>
								<Dropdown.Item onClick={() => setEditedKPI({ data: { id: 0, goalId: goal.id }, action: 'ADD' })}>{t("NewKPI.label")}</Dropdown.Item>
								<Dropdown.Item onClick={() => setEditedGoal({ data: goal, action: 'UPDATE' })}>{t("Edit.label")}</Dropdown.Item>
								<Dropdown.Item onClick={() => setEditedGoal({ data: goal, action: 'DELETE', goalId: goal.id })}>{t("Delete.label")}</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					</td>
				</tr>
				{
					goalVisibility[goal.id] === 'show' &&
					kpis[goal.id]?.map((kpi, kpiIndex) => {
						return renderGoalKPIRow(goal.id, kpi, kpiIndex);
					})
				}
			</>
		);
	};

	const renderGoalCategories = (cat, catIndex) => {
		return (
			<>
				<tr key={`goalCatRow-${catIndex}`} className="topic-row">
					<td className="name-cell">
						<span>
							<span className="mx-2 cursor" onClick={() => showOrHideCategory(cat)}>
								{catVisibility[cat] === 'hide' ? <FaChevronDown /> : <FaChevronUp />}
							</span>
							<span>{goals[cat].category}</span>
						</span>
					</td>
					<td colSpan={7} ></td>
					<td>
						<Dropdown>
							<Dropdown.Toggle variant="blank_outline">
								<BiDotsHorizontalRounded />
							</Dropdown.Toggle>
							<Dropdown.Menu>
								<Dropdown.Item onClick={() => setEditedCategory({
									data: { id: goals[cat].categoryId, category: goals[cat].category },
									action: 'UPDATE'
								})}>{t("Edit.label")}</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>
					</td>
				</tr>
				{
					goals[cat].items?.map((goal, goalIndex) => {
						return renderGoalRow(cat, catIndex, goal, goalIndex);
					})
				}
			</>
		);
	};

	const renderEmptyDataLabel = () => {
		return (
			<tr>
				<td colSpan="9" className="text-center">
					<div className="Nodatafound_box">
						<h6><FcFinePrint className="me-2" />{t("Nodatafound.label")}</h6>
					</div>
				</td>
			</tr>
		)
	};

	const deleteGoal = (id) => {
		KPIService.deleteGoal(id).then(res => {
			if (res.status !== 200) return;

			fetchGoals(currentPage, itemsPerPage);
		}).catch(err => { });
	};

	const deleteKPI = (id, goalId) => {
		KPIService.deleteKPI(id).then(res => {
			if (res.status !== 200) return;

			fetchGoals(currentPage, itemsPerPage);
			fetchKPIsForGoal(goalId);
		}).catch(err => { });
	};

	const renderDeleteConfirmationModal = () => {
		if (editedGoal?.data?.id > 0 && editedGoal?.action === 'DELETE') {
			return (
				<ConfirmationModal
					modalShow={true}
					messageText={t('DeleteGoalConfirmationMessage.label')}
					callbackModal={(val) => {
						deleteGoal(editedGoal?.data?.id);
						setEditedGoal({ data: { id: -1 }, action: 'NONE' });
					}}
					confirmBtn={t("Confirm.label")}
					CancelBtn={t("Cancel.label")}
				/>
			);
		} else if (editedKPI?.data?.id > 0 && editedKPI?.action === 'DELETE') {
			return (
				<ConfirmationModal
					modalShow={true}
					messageText={t('DeleteKPIConfirmationMessage.label')}
					callbackModal={(val) => {
						deleteKPI(editedKPI?.data?.id, editedKPI?.data?.goalId);
						setEditedKPI({ data: { id: -1 }, action: 'NONE' });
					}}
					confirmBtn={t("Confirm.label")}
					CancelBtn={t("Cancel.label")}
				/>
			);
		}
		return <></>;
	};

	const fetchKPIsForGoal = (goalId) => {
		setLoaderDisplay(true);
		KPIService.getKPIs(goalId).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			const k = JSON.parse(JSON.stringify(kpis));
			k[goalId] = res.data.data ?? [];
			setKPIs(k);
		}).catch(err => {
			setLoaderDisplay(false);
		});
	};

	return (
		<div id='kpiListHolder'>
			<KPIHeader
				onNewCategory={() => setEditedCategory({ data: { id: 0 }, action: 'ADD' })}
				onNewGoal={() => setEditedGoal({ data: { id: 0 }, action: 'ADD' })}
				onNewKPI={() => setEditedKPI({ data: { id: 0 }, action: 'ADD' })} />
			<div id='kpiTableHolder' className="mt-2">
				<div className="table-holder mt-0">

					<Table responsive="sm" bordered className="mb-0">
						{renderTableHeader()}
						<tbody>

							{
								Object.keys(goals).length > 0
									?
									Object.keys(goals)?.map((cat, catIndex) => {
										return renderGoalCategories(cat, catIndex);
									})
									:
									renderEmptyDataLabel()
							}
						</tbody>
					</Table>
				</div>
			</div>
			<div className="p-2 List_footer_count">
				<PaginationComponent
					itemsTotalCount={itemsTotalCount}
					itemsPerPage={itemsPerPage}
					currentPage={currentPage}
					setCurrentPage={setCurrentPage}
					alwaysShown={true}
					setItemsPerPage={setItemsPerPage}
					pageClicked={(pageNum, perPage) => {
						fetchGoals(pageNum, perPage);
					}} />
			</div>
			{
				editedCategory.data?.id >= 0 && (editedCategory?.action === 'ADD' || editedCategory?.action === 'UPDATE') &&
				<KPICategoryModal
					categoryToUpdate={editedCategory.data}
					onHide={() => setEditedCategory({ data: { id: -1 }, action: 'NONE' })}
					onSave={() => {
						setEditedCategory({ data: { id: -1 }, action: 'NONE' });
						fetchGoals(currentPage, itemsPerPage);
					}} />
			}
			{
				editedGoal.data?.id >= 0 && (editedGoal?.action === 'ADD' || editedGoal?.action === 'UPDATE') &&
				<KPIGoalModal
					goalToUpdate={editedGoal.data}
					onHide={() => setEditedGoal({ data: { id: -1 }, action: 'NONE' })}
					onSave={() => {
						setEditedGoal({ data: { id: -1 }, action: 'NONE' });
						fetchGoals(currentPage, itemsPerPage);
					}} />
			}
			{
				editedKPI.data?.id >= 0 && (editedKPI?.action === 'ADD' || editedKPI?.action === 'UPDATE') &&
				<KPIModal
					kpiToUpdate={editedKPI.data}
					onHide={() => setEditedKPI({ data: { id: -1 }, action: 'NONE' })}
					onSave={(goalId) => {
						fetchKPIsForGoal(goalId);
						setEditedKPI({ data: { id: -1 }, action: 'NONE' });
					}} />
			}
			{
				editedAction.data?.id >= 0 && (editedAction?.action === 'ADD' || editedAction?.action === 'UPDATE' || editedAction?.action === 'VIEW_ACTIONS') &&
				<KPIActionModal
					mode={editedAction?.action === 'VIEW_ACTIONS' ? 'VIEW_ACTIONS' : 'FORM'}
					kpiId={editedAction.data.kpiId}
					actionToUpdate={editedAction.data}
					onHide={() => setEditedAction({ data: { id: -1 }, action: 'NONE' })}
					onSave={() => {
						if (editedAction?.action !== 'VIEW_ACTIONS') {
							fetchKPIsForGoal(editedAction.data.goalId);
						}
						setEditedAction({ data: { id: -1 }, action: 'NONE' });
					}} />
			}
			{renderDeleteConfirmationModal()}
		</div>
	);
};

export default withTranslation()(KPIList);
